import { useEffect, useRef, useState, type FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { requestOtp, verifyOtp } from '@/shared/api/auth';
import { useAuthStore } from '@/shared/auth/store';

type Step = 'email' | 'code';
type Mode = 'login' | 'signup';

interface LocationState {
  from?: { pathname: string };
}

const RESEND_SECONDS = 60;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** 카카오 공식 심볼(말풍선). 버튼 색상 규정: #FEE500 배경 + 검정 심볼·라벨. */
function KakaoSymbol() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 3C6.48 3 2 6.54 2 10.9c0 2.8 1.86 5.26 4.66 6.66-.15.52-.96 3.32-.99 3.54 0 0-.02.17.09.23.11.07.24.02.24.02.32-.04 3.66-2.4 4.24-2.81.57.08 1.16.13 1.76.13 5.52 0 10-3.54 10-7.9S17.52 3 12 3Z"
      />
    </svg>
  );
}

/** 구글 공식 'G' 멀티컬러 심볼. */
function GoogleSymbol() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65Z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19Z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48Z"
      />
    </svg>
  );
}

/** 네이버 공식 'N' 심볼. 버튼 색상 규정: #03C75A 배경 + 흰색 심볼·라벨. */
function NaverSymbol() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.273 12.845 7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727v12.845Z"
      />
    </svg>
  );
}

const SOCIAL_PROVIDERS = [
  {
    id: 'kakao',
    label: '카카오',
    icon: <KakaoSymbol />,
    enabled: import.meta.env.VITE_KAKAO_ENABLED === 'true',
    className: 'bg-[#FEE500] text-black hover:bg-[#FEE500]/90',
  },
  {
    id: 'google',
    label: 'Google',
    icon: <GoogleSymbol />,
    enabled: import.meta.env.VITE_GOOGLE_ENABLED === 'true',
    className: 'border border-surface-border bg-surface text-ink hover:bg-canvas',
  },
  {
    id: 'naver',
    label: '네이버',
    icon: <NaverSymbol />,
    enabled: import.meta.env.VITE_NAVER_ENABLED === 'true',
    className: 'bg-[#03C75A] text-white hover:bg-[#03C75A]/90',
  },
] as const;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const pendingInviteToken = useAuthStore((s) => s.pendingInviteToken);

  const [mode, setMode] = useState<Mode>('login');
  const [step, setStep] = useState<Step>('email');
  const [emailInput, setEmailInput] = useState('');
  const [code, setCode] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resendRemaining, setResendRemaining] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendRemaining <= 0) return;
    const t = window.setInterval(() => {
      setResendRemaining((v) => (v > 0 ? v - 1 : 0));
    }, 1000);
    return () => window.clearInterval(t);
  }, [resendRemaining]);

  const email = emailInput.trim().toLowerCase();
  useEffect(() => {
    if (step === 'code') {
      window.setTimeout(() => codeInputRef.current?.focus(), 0);
    }
  }, [step]);
  const emailValid = EMAIL_PATTERN.test(email);

  async function onRequestOtp(e: FormEvent) {
    e.preventDefault();
    setEmailError(null);
    if (!emailValid) {
      setEmailError('올바른 이메일 주소를 입력해주세요.');
      return;
    }
    setLoading(true);
    try {
      await requestOtp(email);
      setStep('code');
      setCode('');
      setCodeError(null);
      setResendRemaining(RESEND_SECONDS);
    } catch {
      setEmailError('인증 코드 발송에 실패했어요. 이메일을 확인해주세요.');
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    if (resendRemaining > 0 || loading) return;
    setLoading(true);
    try {
      await requestOtp(email);
      setCode('');
      setCodeError(null);
      setResendRemaining(RESEND_SECONDS);
    } catch {
      setCodeError('재전송에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }

  async function onVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setCodeError(null);
    if (code.length !== 6) {
      setCodeError('6자리 인증번호를 입력해주세요.');
      return;
    }
    if (mode === 'signup' && !nickname.trim()) {
      setCodeError('닉네임을 입력해주세요.');
      return;
    }
    setLoading(true);
    try {
      const res = await verifyOtp(email, code, mode === 'signup' ? nickname.trim() : undefined);
      setAuth(res.accessToken, res.user);
      const state = location.state as LocationState | null;
      if (pendingInviteToken) {
        navigate(`/invite/${pendingInviteToken}`, { replace: true });
      } else if (state?.from?.pathname && state.from.pathname !== '/login') {
        navigate(state.from.pathname, { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    } catch (err) {
      const errorCode = isAxiosError(err)
        ? (err.response?.data as { error?: { code?: string } } | undefined)?.error?.code
        : undefined;
      if (errorCode === 'VALIDATION_NICKNAME_REQUIRED') {
        // 미가입 이메일의 로그인 시도 — 코드는 소비되지 않았으므로 그대로 가입을 이어간다
        setMode('signup');
        setNotice('아직 가입되지 않은 이메일이에요. 닉네임을 입력하면 같은 코드로 바로 가입돼요.');
      } else {
        setCodeError('인증에 실패했어요. 코드를 다시 확인해주세요.');
      }
    } finally {
      setLoading(false);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setStep('email');
    setCode('');
    setNickname('');
    setCodeError(null);
    setEmailError(null);
    setNotice(null);
  }

  const resendLabel = `${Math.floor(resendRemaining / 60)
    .toString()
    .padStart(2, '0')}:${(resendRemaining % 60).toString().padStart(2, '0')}`;

  return (
    <section className="flex min-h-[calc(100vh-96px)] flex-col px-1 py-5">

      {step === 'email' ? (
        <>
          <header className="mb-5">
            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1.5 text-[10px] font-semibold text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
              친구 모임 올인원
            </div>
            <h1 className="text-[23px] font-semibold leading-[1.18] tracking-[-0.04em] text-ink">
              친구와의 약속,
              <br />
              <span className="text-primary">더 단순하게</span>
            </h1>
            <p className="mt-2 text-[13px] font-medium leading-5 text-ink-muted">
              모임, 투표, 장소, 일정, 정산까지
              <br />
              하나의 흐름으로 관리해요.
            </p>
          </header>

          {pendingInviteToken && (
            <div className="mb-3 rounded-[14px] bg-primary-soft px-3.5 py-3 text-small font-semibold text-primary">
              로그인 후 초대 링크로 자동 이동합니다.
            </div>
          )}

          {SOCIAL_PROVIDERS.some((p) => p.enabled) && (
            <>
              <div className="space-y-2">
                {SOCIAL_PROVIDERS.filter((p) => p.enabled).map((p) => (
                  <a
                    key={p.id}
                    href={`/api/v1/auth/${p.id}/login`}
                    className={`inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-[13px] text-[14px] font-semibold transition focus-visible:outline-none focus-visible:shadow-focus-ring ${p.className}`}
                    aria-label={`${p.label}로 로그인`}
                  >
                    {p.icon}
                    {p.label}로 계속하기
                  </a>
                ))}
              </div>

              <div
                className="my-3.5 flex items-center gap-2.5"
                role="separator"
                aria-label="또는 이메일로"
              >
                <span className="h-px flex-1 bg-surface-border" aria-hidden="true" />
                <span className="text-[11px] font-medium text-ink-disabled">또는 이메일로</span>
                <span className="h-px flex-1 bg-surface-border" aria-hidden="true" />
              </div>
            </>
          )}

          <form
            onSubmit={onRequestOtp}
            className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5"
          >
            <h2 className="mb-3 text-[15px] font-semibold tracking-[-0.02em] text-ink">
              {mode === 'login' ? '이메일 로그인' : '이메일 회원가입'}
            </h2>
            <label htmlFor="email" className="mb-2 block text-[11px] font-semibold text-ink-muted">
              이메일
            </label>
            <input
              id="email"
              type="email"
              name="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              required
              aria-required="true"
              maxLength={255}
              className="h-[42px] w-full rounded-[13px] border border-surface-border bg-canvas px-3.5 text-[14px] font-semibold text-ink outline-none transition focus:border-primary focus:shadow-focus-ring"
            />
            {emailError && (
              <p role="alert" className="mt-2 text-small font-medium text-error">
                {emailError}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || !emailValid}
              className="mt-2.5 min-h-11 w-full rounded-[13px] bg-[image:var(--hero-grad)] text-[14px] font-semibold text-white shadow-[var(--hero-glow)] transition disabled:bg-primary-light/70 focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              {loading ? '요청 중…' : '인증 코드 받기'}
            </button>
          </form>

          <p className="mt-3 text-center text-[11px] leading-5 text-ink-muted">
            {mode === 'login' ? (
              <>
                아직 계정이 없나요?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  이메일로 회원가입
                </button>
              </>
            ) : (
              <>
                이미 계정이 있나요?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('login')}
                  className="font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
                >
                  로그인
                </button>
              </>
            )}
          </p>
          <p className="mt-1.5 text-center text-[10px] leading-4 text-ink-disabled">
            계속하면{' '}
            <a href="/terms.html" className="font-medium text-ink-muted underline underline-offset-2">
              서비스 이용약관
            </a>
            과{' '}
            <a href="/privacy.html" className="font-medium text-ink-muted underline underline-offset-2">
              개인정보 처리방침
            </a>
            에 동의하게 됩니다.
          </p>
        </>
      ) : (
        <>
          <header className="mb-5">
            <h1 className="text-[23px] font-semibold leading-[1.18] tracking-[-0.04em] text-ink">
              메일로 보낸
              <br />
              <span className="text-primary">코드를 입력해요</span>
            </h1>
            <p className="mt-2 text-[13px] font-medium leading-5 text-ink-muted">
              입력한 이메일로 6자리 인증 코드를 보냈어요.
            </p>
          </header>

          <form
            onSubmit={onVerifyOtp}
            className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-4"
          >
            <div className="mb-3 inline-flex rounded-full bg-canvas px-3 py-1.5 text-[11px] font-semibold text-ink-muted">
              {email}
            </div>
            {notice && (
              <p
                role="status"
                className="mb-3 rounded-[13px] bg-primary-soft px-3 py-2 text-small font-semibold text-primary"
              >
                {notice}
              </p>
            )}
            <label htmlFor="code" className="mb-2 block text-[11px] font-semibold text-ink-muted">
              인증 코드
            </label>
            <button
              type="button"
              className="mb-2 flex gap-1.5 focus-visible:outline-none focus-visible:shadow-focus-ring"
              onClick={() => codeInputRef.current?.focus()}
              aria-label="인증 코드 입력"
            >
              {Array.from({ length: 6 }).map((_, index) => {
                const digit = code[index] ?? '';
                const active = index === code.length;
                return (
                  <span
                    key={index}
                    className={`flex h-11 w-9 items-center justify-center rounded-xl border-[1.5px] text-[18px] font-semibold ${
                      active
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-surface-border bg-canvas text-ink'
                    }`}
                    aria-hidden="true"
                  >
                    {digit}
                  </span>
                );
              })}
            </button>
            <input
              ref={codeInputRef}
              id="code"
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              aria-required="true"
              maxLength={6}
              className="sr-only"
            />
            {codeError && (
              <p role="alert" className="mt-2 text-small font-medium text-error">
                {codeError}
              </p>
            )}
            <div aria-live="polite" className="mt-2 flex items-center justify-between text-[11px]">
              <span className="font-semibold text-warning">
                {resendRemaining > 0 ? `${resendLabel} 남음` : '재전송 가능'}
              </span>
              <button
                type="button"
                onClick={onResend}
                disabled={resendRemaining > 0 || loading}
                className="font-semibold text-primary disabled:text-ink-disabled focus-visible:outline-none focus-visible:shadow-focus-ring"
              >
                다시 보내기
              </button>
            </div>

            {mode === 'signup' && (
              <div className="mt-3">
                <label
                  htmlFor="nickname"
                  className="mb-2 block text-[11px] font-semibold text-ink-muted"
                >
                  닉네임
                </label>
                <input
                  id="nickname"
                  type="text"
                  name="nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="친구야"
                  required
                  aria-required="true"
                  maxLength={20}
                  className="h-[42px] w-full rounded-[13px] border border-surface-border bg-canvas px-3.5 text-[14px] font-semibold text-ink outline-none transition focus:border-primary focus:shadow-focus-ring"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || code.length !== 6 || (mode === 'signup' && !nickname.trim())}
              className="mt-3 h-[43px] w-full rounded-[13px] bg-[image:var(--hero-grad)] text-[14px] font-semibold text-white shadow-[var(--hero-glow)] transition disabled:bg-primary-light/70 focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              {loading ? '확인 중…' : mode === 'login' ? '로그인하기' : '가입하기'}
            </button>
          </form>

          <button
            type="button"
            onClick={() => {
              setStep('email');
              setCode('');
              setCodeError(null);
            }}
            className="mt-3 w-full text-center text-[11px] font-semibold text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            이메일을 다시 입력할게요
          </button>
        </>
      )}
    </section>
  );
}
